import React, { useEffect, useRef, useState } from 'react';
import { Bell, Search, ChevronDown, LogOut, Phone, RefreshCcw, Settings } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [show, setShow] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { user, logout, currentProfile, switchProfile, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  const isKid = !!currentProfile?.is_kid;
  const linkCls = ({ isActive }) =>
    `cursor-pointer transition-colors ${isActive ? 'font-semibold text-white' : 'text-white/90 hover:text-white/70'}`;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 md:px-14 py-4 transition-colors duration-500 ${
        show ? 'bg-[#141414]' : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
    >
      <div className="flex items-center gap-8">
        <Link to="/">
          <span
            className="cursor-pointer select-none text-2xl md:text-3xl font-black tracking-tight"
            style={{
              fontFamily: "'Bebas Neue', 'Oswald', Impact, sans-serif",
              color: '#e11d48', letterSpacing: '0.03em',
              textShadow: '0 2px 14px rgba(225,29,72,0.4)',
            }}
          >
            NOVA<span style={{ color: '#ffffff' }}>REEL</span><span style={{ color: '#e11d48' }}>TV</span>
          </span>
        </Link>
        <ul className="hidden md:flex gap-5 text-sm">
          <li><NavLink to="/" end className={linkCls}>Home</NavLink></li>
          {!isKid && <li><NavLink to="/kids" className={linkCls}>Kids</NavLink></li>}
          <li><NavLink to="/mylist" className={linkCls}>My List</NavLink></li>
          <li><NavLink to="/search" className={linkCls}>Search</NavLink></li>
        </ul>
      </div>

      <div className="flex items-center gap-4 md:gap-5 text-white">
        <button
          type="button"
          onClick={() => navigate('/search')}
          className="p-1 rounded hover:bg-white/10 transition-colors"
          aria-label="Search"
        >
          <Search className="w-5 h-5 pointer-events-none" />
        </button>
        <Bell className="w-5 h-5 cursor-pointer" />
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            data-testid="navbar-profile-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="flex items-center gap-1 cursor-pointer p-1 -m-1 rounded hover:bg-white/10 transition-colors"
          >
            {currentProfile?.avatar ? (
              <img src={currentProfile.avatar} alt={currentProfile.name} className="w-8 h-8 rounded pointer-events-none bg-white/10 border border-white/10" />
            ) : (
              <div className="w-8 h-8 rounded bg-gradient-to-br from-[#e11d48] to-[#7f1d1d] flex items-center justify-center pointer-events-none">
                <Phone className="w-4 h-4 text-white" />
              </div>
            )}
            <ChevronDown className={`w-4 h-4 pointer-events-none transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-3 w-64 bg-black/95 border border-white/10 rounded shadow-2xl py-2 text-sm z-[70]">
              {currentProfile && (
                <div className="px-4 py-2 flex items-center gap-3">
                  <img src={currentProfile.avatar} alt={currentProfile.name} className="w-8 h-8 rounded bg-white/10" />
                  <div>
                    <div className="text-white font-medium">{currentProfile.name}</div>
                    {currentProfile.is_kid && (
                      <div className="text-[10px] text-emerald-400 uppercase">Kid profile</div>
                    )}
                  </div>
                </div>
              )}
              <button
                onClick={() => {
                  setMenuOpen(false);
                  switchProfile();
                  navigate('/profiles');
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-white/90 hover:bg-white/10 transition-colors"
              >
                <RefreshCcw className="w-4 h-4" /> Switch profile
              </button>
              {isAdmin && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/admin');
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left text-amber-300 hover:bg-white/10 transition-colors"
                >
                  <Settings className="w-4 h-4" /> Admin · Sponsorship
                </button>
              )}
              <div className="border-t border-white/10 my-1" />
              <div className="px-4 py-2 text-white/60">
                Signed in as
                <div className="text-white font-medium">{user?.phone}</div>
              </div>
              <div className="border-t border-white/10 my-1" />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-white/90 hover:bg-white/10 transition-colors"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
